import { createStore } from 'zustand/vanilla';

import type { JourneyNode, NodeType } from '../../../core/entities/node';
import {
	seedOutlineVisibleTypes,
	type OutlineGroupBy,
	type OutlineSortKey,
} from '../../../core/policies/workspace/derive-outline-rows';

export interface OutlineWorkspaceState {
	isSeeded: boolean;
	searchQuery: string;
	sortKey: OutlineSortKey;
	sortDirection: 'asc' | 'desc';
	groupBy: OutlineGroupBy;
	collapsedGroupIds: string[];
	localVisibleTypes: NodeType[];
	inlineEditingNodeId: string | null;
	inlineDraft: JourneyNode | null;
	seedFromVisibleType(visibleType: NodeType | 'All'): void;
	setSearchQuery(query: string): void;
	setSortKey(key: OutlineSortKey): void;
	setSortDirection(direction: 'asc' | 'desc'): void;
	toggleGroupByType(): void;
	toggleVisibleType(type: NodeType): void;
	toggleCollapsedGroup(groupId: string): void;
	startInlineEdit(node: JourneyNode): void;
	updateInlineDraft(node: JourneyNode): void;
	clearInlineEdit(): void;
}

export function createOutlineWorkspaceStore() {
	return createStore<OutlineWorkspaceState>((set) => ({
		isSeeded: false,
		searchQuery: '',
		sortKey: 'displayName',
		sortDirection: 'asc',
		groupBy: 'type',
		collapsedGroupIds: [],
		localVisibleTypes: ['Human', 'Project', 'Occupation'],
		inlineEditingNodeId: null,
		inlineDraft: null,
		seedFromVisibleType(visibleType) {
			set((state) => {
				if (state.isSeeded) {
					return state;
				}

				return {
					isSeeded: true,
					localVisibleTypes: seedOutlineVisibleTypes(visibleType),
				};
			});
		},
		setSearchQuery(searchQuery) {
			set({ searchQuery });
		},
		setSortKey(sortKey) {
			set({ sortKey });
		},
		setSortDirection(sortDirection) {
			set({ sortDirection });
		},
		toggleGroupByType() {
			set((state) => ({ groupBy: state.groupBy === 'type' ? 'none' : 'type' }));
		},
		toggleVisibleType(type) {
			set((state) => {
				if (state.localVisibleTypes.includes(type)) {
					const next = state.localVisibleTypes.filter((value) => value !== type);
					if (next.length === 0) {
						return state;
					}

					return { localVisibleTypes: next };
				}

				return { localVisibleTypes: [...state.localVisibleTypes, type] };
			});
		},
		toggleCollapsedGroup(groupId) {
			set((state) => ({
				collapsedGroupIds: state.collapsedGroupIds.includes(groupId)
					? state.collapsedGroupIds.filter((value) => value !== groupId)
					: [...state.collapsedGroupIds, groupId],
			}));
		},
		startInlineEdit(node) {
			set({
				inlineEditingNodeId: node.nodeId,
				inlineDraft: node,
			});
		},
		updateInlineDraft(inlineDraft) {
			set({ inlineDraft });
		},
		clearInlineEdit() {
			set({
				inlineEditingNodeId: null,
				inlineDraft: null,
			});
		},
	}));
}